import React from 'react';
import Icon from '@/components/atoms/icon/icon';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import TransportModeDetails from '@/components/blocks/transport-mode/transport-mode-details/transport-mode-details';
import AnchorLink from '@/components/molecules/anchor-link/anchor-link';
import FlightSelector from '@/components/organisms/flight-selector/flight-selector';
import { useHotelContext } from '@/libraries/contexts/hotel-context/hotel-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import useDateFormatter from '@/libraries/formatters/use-date-formatter/use-date-formatter';
import styles from './transport-mode.module.scss';

export interface TransportModeProps {
  /** The headline to show */
  headline: Content.Components['TransportModeModuleDto']['headline'];
  /** The UUID of the module */
  id: Content.Components['TransportModeModuleDto']['id'];
  /** The text to show for the link */
  linkText: Content.Components['TransportModeModuleDto']['linkText'];
  /** The UUID of the current page */
  pageId: Content.Components['TransportModeModuleDto']['pageId'];
  /** The text to show as a description */
  text: Content.Components['TransportModeModuleDto']['text'];
  /** The transport mode information */
  transportMode: Content.Components['TransportModeModuleDto']['transportMode'];
  /** The travel plan information */
  transportModeDetails: Content.Components['TransportModeModuleDto']['transportModeDetails'];
}

const TransportMode = ({
  headline,
  linkText,
  text,
  transportMode,
  transportModeDetails,
}: TransportModeProps) => {
  const { t } = useI18n();
  const { dateFormatter } = useDateFormatter();
  const { setIsOpenTravelPlan } = useHotelContext();
  const { offerDetails } = usePageDetailsContext();

  const renderAdditionalInfo = () => {
    const hasAdditionalInfo = offerDetails?.transportMode !== 'plane';

    if (!hasAdditionalInfo) return null;

    let text;
    let date;

    if (offerDetails?.transportMode === 'own') {
      text = t('items.common.words.arrival');
      date = dateFormatter(offerDetails?.outboundArrivalDate).format(
        'ddd DD-MM-YY',
      );
    } else {
      text = t('items.common.words.departure');
      date = dateFormatter(offerDetails?.outboundDepartureDate).format(
        'ddd DD-MM-YY',
      );
    }

    return (
      <div className={styles.infoWrapper}>
        <div className={styles.departure}>
          <Icon name="Calendar" size={16} />

          <Typography className={styles.info} tagStyle="bodyMedium">
            {text}
          </Typography>
        </div>

        <Typography className={styles.date} tagStyle="bodyLargeBold">
          {date}
        </Typography>
      </div>
    );
  };

  const renderContent = () => {
    if (transportMode.$type === 'TransportModePlaneDto') {
      return (
        <FlightSelector
          {...transportMode.flightSelector}
          headline={headline}
          linkText={linkText}
          text={text}
        />
      );
    }

    return (
      <div className={styles.content}>
        <div className={styles.textWrapper}>
          <Typography className={styles.headline} tagStyle="bodyMediumBold">
            {headline}
          </Typography>

          <div className={styles.text}>
            <RichText text={text} />

            {renderAdditionalInfo()}

            {linkText && (
              <AnchorLink
                className={styles.link}
                icon="ArrowRight"
                onClick={() => {
                  setIsOpenTravelPlan(true);
                }}
                text={linkText}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className={styles.component}>{renderContent()}</div>

      <TransportModeDetails {...transportModeDetails} />
    </React.Fragment>
  );
};

TransportMode.displayName = 'TransportMode';

export default TransportMode;
